<template>
  <div class="mb-4 flex justify-end py-2">
    <div class="flex justify-between space-x-2">
      <DateFilter
        :model-value="timePeriod"
        @update:model-value="setTimePeriod"
      />
    </div>
  </div>
  <BaseCard class="mb-4">
    <div
      class="mb-2 flex w-full flex-wrap items-center justify-end space-x-2 pt-2"
    >
      <div
        class="mb-2 flex flex-grow items-center space-x-2"
      >
        <div
          class="grid w-full grid-cols-2 gap-2 md:grid-cols-4"
        >
          <ReportFilterSelect
            v-model="localQueries[0].cashierId"
            label="Cashier"
            :options="cashierOptions"
            class="w-full"
          />
          <ReportDeviceFilter
            v-model="localQueries[0].deviceId"
          />
          <ReportFilterSelect
            v-model="localQueries[0].categoryId"
            label="Category"
            :options="categoryOptions"
            class="w-full"
          />
          <!-- ref: hospUnhide -->
          <ReportFilterSelect
            v-model="localQueries[0].productTagId"
            label="Product Tag"
            :options="productTagOptions"
            class="w-full"
          >
            <template #option="{ option }">
              <ProductTagChip
                :product-tag-id="option.value as string"
              />
            </template>
          </ReportFilterSelect>
        </div>
      </div>
    </div>
  </BaseCard>
  <BaseCard class="relative">
    <div class="flex w-full flex-col">
      <div class="flex items-center justify-between py-2">
        <ReportFilterLimit
          v-model="limit"
          label="Limit"
          class="w-full"
        />
        <ReportViewToggle
          v-model="graphView"
          :options="reportViewOptions"
        />
      </div>
      <div
        class="md:h-128 h-64 w-full overflow-hidden sm:h-96"
      >
        <template v-if="graphView === 'soldCount'">
          <ChartBarCategory
            id="top-products-soldcount-graph"
            :labels="labels"
            :datasets="soldCountDatasets"
            x-axis-type="number"
            :currency="tenant.currency"
            @hover-datapoint="handleHoverDatapoint"
          />
        </template>
        <template v-else-if="graphView === 'revenue'">
          <ChartBarCategory
            id="top-products-revenue-graph"
            :labels="labels"
            :datasets="revenueDatasets"
            x-axis-type="currency"
            :currency="tenant.currency"
            @hover-datapoint="handleHoverDatapoint"
          />
        </template>
      </div>

      <div
        class="bg-background-fg/5 flex-shrink-0 rounded-lg p-2"
      >
        <template v-if="mainDataset">
          <div
            class="grid grid-cols-1 gap-2 overflow-hidden sm:grid-cols-2"
          >
            <template
              v-for="(item, index) in mainDataset"
              :key="index"
            >
              <div
                :class="[
                  'grid grid-cols-4 gap-4',
                  hoveredData?.index === undefined ||
                  hoveredData?.index === index
                    ? ''
                    : 'opacity-50',
                ]"
              >
                <div
                  class="col-span-2 flex items-center space-x-2"
                >
                  <ColorItem
                    rounded
                    :value="getColorForIndex(index)"
                  />
                  <p
                    class="line-clamp-2 text-sm font-medium"
                  >
                    {{ item.productName }}
                  </p>
                </div>

                <div>
                  <p
                    class="text-background-fg/70 text-center text-sm leading-tight"
                  >
                    Sold
                  </p>
                  <p
                    class="line-clamp-1 text-center font-semibold"
                  >
                    {{ item.soldCount }}
                  </p>
                </div>
                <div>
                  <p
                    class="text-background-fg/70 text-center text-sm leading-tight"
                  >
                    Revenue
                  </p>
                  <p
                    class="line-clamp-1 text-center font-semibold"
                  >
                    {{
                      formatToMerchantCurrencyString(
                        item.revenue,
                        {
                          compact: true,
                        }
                      )
                    }}
                  </p>
                </div>
              </div>
            </template>
          </div>
        </template>
      </div>
    </div>
  </BaseCard>
</template>

<script setup lang="ts">
import type { TopProductsReportQuery } from "@/services/reports/retrieveTopProductsReport"
import type { HoveredDataPoint } from "@/components/ChartLineDate.vue"

const { tenant } = useTenant()
const { asOptions: categoryOptions } = useCategories()
const { options: cashierOptions } = useCashierOptions()
const { asOptions: productTagOptions } = useProductTags()
const { formatToMerchantCurrencyString } = useCurrency()
const emptyLocalQuery = {
  cashierId: undefined,
  categoryId: undefined,
}

const limit = ref(10)

const { queries, localQueries, timePeriod, setTimePeriod } =
  useReportQuery<TopProductsReportQuery>(emptyLocalQuery)

const queryResults = useTopProductsReports(queries)

const rawDatasets = computed(() =>
  queryResults.map(({ data }) =>
    data ? data.dataset.slice(0, limit.value) : undefined
  )
)

const mainDataset = computed(() => rawDatasets.value[0])
const labels = computed(
  () =>
    rawDatasets.value?.[0]?.map(
      ({ productName }) => productName
    ) || []
)

const soldCountDatasets = computed(() =>
  rawDatasets.value.map((rawDataset) => {
    return {
      label: "",
      data:
        rawDataset?.map(({ soldCount }) => soldCount) || [],
      color: undefined,
    }
  })
)

const revenueDatasets = computed(() =>
  rawDatasets.value.map((rawDataset) => {
    return {
      label: "",
      data: rawDataset?.map(({ revenue }) => revenue) || [],
      color: undefined,
    }
  })
)

const handleHoverDatapoint = (
  hoveredData_: HoveredDataPoint
) => {
  hoveredData.value = hoveredData_
}

const hoveredData = ref<HoveredDataPoint>()

// const isExporting = ref(false)
// const onExport = async (
//   query: TopProductsReportQuery,
//   format?: FileExportFormat
// ) => {
//   isExporting.value = true
//   try {
//     await exportTopProductsReport(query, format)
//   } finally {
//     isExporting.value = false
//   }
// }

const reportViewOptions = [
  {
    value: "soldCount",
    label: "Count",
  },
  {
    value: "revenue",
    label: "Revenue",
  },
] as const

const graphView =
  ref<(typeof reportViewOptions)[number]["value"]>(
    "soldCount"
  )

const getColorForIndex = (index: number) => {
  const numberOfColors = Object.values(COLORS_MAP).length
  const colorIndex = index % numberOfColors
  return `rgb(var(${
    Object.values(COLORS_MAP)[colorIndex].value
  }))`
}
</script>
